import React from "react";
import { Play, Pause, AlertCircle } from "lucide-react";

interface EmbedControlsProps {
  isLive: boolean;
  isConnected: boolean;
  isConnecting: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
  connectionError: Error | null;
  theme: "light" | "dark";
}

export function EmbedControls({
  isLive,
  isConnected,
  isConnecting,
  onConnect,
  onDisconnect,
  connectionError,
  theme,
}: EmbedControlsProps) {
  const mutedTextColor = theme === "dark" ? "text-[#4fa935]" : "text-gray-600";
  const buttonBgColor = theme === "dark"
    ? "bg-[#6adf47] hover:bg-[#5ac937]"
    : "bg-gray-900 hover:bg-gray-800";
  const buttonTextColor = theme === "dark" ? "text-[#081506]" : "text-white";
  const errorColor = theme === "dark" ? "text-red-400" : "text-red-600";

  if (!isLive) {
    return null;
  }

  return (
    <div className="mt-4 space-y-3">
      {/* Connection Error */}
      {connectionError && connectionError.name !== 'NotAllowedError' && (
        <div className={`text-center text-xs flex items-center justify-center gap-2 ${errorColor}`}>
          <AlertCircle className="w-3 h-3" />
          <span>CONNECTION ERROR</span>
        </div>
      )}

      {/* Play/Pause Button */}
      <div className="flex justify-center">
        <button
          onClick={() => {
            if (!isConnected && !isConnecting) {
              onConnect();
            } else if (isConnected) {
              onDisconnect();
            }
          }}
          disabled={isConnecting}
          className={`
            ${buttonBgColor} ${buttonTextColor}
            px-6 py-2 rounded-md font-bold text-sm
            transition-all duration-200
            disabled:opacity-50 disabled:cursor-not-allowed
            flex items-center gap-2
          `}
        >
          {isConnecting ? (
            <>
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              <span>CONNECTING...</span>
            </>
          ) : isConnected ? (
            <>
              <Pause className="w-4 h-4" />
              <span>STOP</span>
            </>
          ) : (
            <>
              <Play className="w-4 h-4" />
              <span>PLAY</span>
            </>
          )}
        </button>
      </div>

      {/* Autoplay blocked message */}
      {connectionError && connectionError.name === 'NotAllowedError' && (
        <div className={`text-center text-xs ${mutedTextColor}`}>
          Click play to start audio
        </div>
      )}
    </div>
  );
}
